import { Col, Divider, Row, Typography } from 'antd';
import { CfSimCompareResult } from 'domain/entities/SimulateEntity';
import { commonHelpers } from 'utilities/helpers';
import { TAX_RATE } from 'utilities/constants/COMMON';

export const ItemComponent = ({
    value,
    type = 'number',
    suffix,
}: {
    value?: any;
    type?: string;
    suffix?: string;
}) => {
    const displayValue = (value: any) => {
        if (!value && value !== 0) {
            if (type === 'number') return 0;
            return '-';
        }
        if (type == 'number' && !isNaN(value)) {
            if (suffix === '%') {
                return Number(value).toLocaleString('en-US', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                });
            }
            return commonHelpers.formatMoney(Number(value), 1);
        }
        return value;
    };

    return (
        <>
            <Typography.Text className="text-right">
                <span className="sim-value">{`${displayValue(value)} `}</span>
                {suffix && <span className="sim-suffix">{suffix}</span>}
            </Typography.Text>
        </>
    );
};
const feeRate = 0.03
const fixedFeeAmount = 60000
const simulatorConsumptionTaxRate = TAX_RATE.INCLUDE;
const BADGE_NAME = {
    10: 'キャッシュフローシミュレーション',
    100: '購入申込時',
    110: '確定',
};
export const StatusBadge = ({ type }: { type?: number }) => {
    return (
        <>
            {type == 10 && (
                <div className={`badge py-3 px-2 w-fit badge-${type}`}>{BADGE_NAME[type]}</div>
            )}
            {type == 100 && (
                <div className={`badge py-3 px-2 w-fit badge-${type} bg-[#EBF7FF] text-[#1F8DDD]`}>
                    {BADGE_NAME[type]}
                </div>
            )}
            {type == 110 && (
                <div className={`badge py-3 px-2 w-fit badge-${type} bg-[#DEF6E8] text-[#01AA6D]`}>
                    {BADGE_NAME[type]}
                </div>
            )}
        </>
    );
};

const TITLE_COL_SPAN = 4;
const CONTENT_COL_SPAN = 10;

export const SimCompareComponent = ({ data }: { data?: CfSimCompareResult }) => {
    return (
        <>
            <Row className="items-start w-full" gutter={[0, 12]}>
                <Row className="w-full sim-title-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title"></Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        {data?.cfSim1?.name ? (
                            <div>{data?.cfSim1?.name}</div>
                        ) : (
                            <div>{data?.cfSim1?.simulationTitle}</div>
                        )}
                        <StatusBadge type={data?.cfSim1?.simCalcDiv} />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        {data?.cfSim2?.name ? (
                            <div>{data?.cfSim2?.name}</div>
                        ) : (
                            <div>{data?.cfSim2?.simulationTitle}</div>
                        )}
                        <StatusBadge type={data?.cfSim2?.simCalcDiv} />
                    </Col>
                </Row>

                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        毎月の収支
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim1?.monthlyBalanceOfPayments || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim2?.monthlyBalanceOfPayments || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        年間の収支
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim1?.yearlyBalanceOfPayments || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim2?.yearlyBalanceOfPayments || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        ローン金額
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim1?.loanAmount || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim2?.loanAmount || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        賃貸管理手数料
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim1?.managementOutsourcingFee || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim2?.managementOutsourcingFee || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        月々のお支払い
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim1?.monthlyPaymentAmount || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent
                            value={data?.cfSim2?.monthlyPaymentAmount || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        管理費等
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim1?.totalManagementFee || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim2?.totalManagementFee || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        家賃収入
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim1?.rentalIncome || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim2?.rentalIncome || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col title">
                        家賃収入
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim1?.rentalIncome || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col content">
                        <ItemComponent value={data?.cfSim2?.rentalIncome || 0} suffix="円" />
                    </Col>
                </Row>
                <Divider />

                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={24} className="w-full sim-compare-col title title-bold">
                        入力条件
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        購入価格
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent
                            value={data?.cfSim1?.purchasePriceIncludingBrokerageFee || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent
                            value={data?.cfSim2?.purchasePriceIncludingBrokerageFee || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        自己資金
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.personalFunds + "0"  || 0} suffix="万円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.personalFunds + "0" || 0} suffix="万円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        返済期間
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.repaymentPeriod || 0} suffix="年" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.repaymentPeriod || 0} suffix="年" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        仲介手数料
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.brokerageFee || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.brokerageFee || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        賃料
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.rent || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.rent || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        共益費
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.commonServiceFee || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.commonServiceFee || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        管理費
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.managementFee || 0} suffix="円" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.managementFee || 0} suffix="円" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        修繕積立金
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent
                            value={data?.cfSim1?.reserveFundForRepairs || 0}
                            suffix="円"
                        />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent
                            value={data?.cfSim2?.reserveFundForRepairs || 0}
                            suffix="円"
                        />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        金融機関
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.financialInstitutionNameDisp} type="text" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.financialInstitutionNameDisp} type="text" />
                    </Col>
                </Row>
                <Row className="w-full sim-compare-row" gutter={[12, 12]}>
                    <Col span={TITLE_COL_SPAN} className="w-full sim-compare-col small title">
                        金利
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim1?.interestRate} suffix="%" />
                    </Col>
                    <Col span={CONTENT_COL_SPAN} className="w-full sim-compare-col small content">
                        <ItemComponent value={data?.cfSim2?.interestRate} suffix="%" />
                    </Col>
                </Row>
            </Row>
        </>
    );
};
